import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Users, UserPlus, Phone, Calendar, CheckCircle, XCircle, 
  Search, Download, Plus, X, Edit2, Trash2, Tag, MessageSquare,
  ChevronLeft, Filter
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAGES = [
  { value: 'new_lead', label: 'New Lead', color: '#3B82F6', icon: UserPlus },
  { value: 'contacted', label: 'Contacted', color: '#8B5CF6', icon: Phone },
  { value: 'booked_call', label: 'Booked Call', color: '#F59E0B', icon: Calendar },
  { value: 'attended_call', label: 'Attended Call', color: '#10B981', icon: CheckCircle },
  { value: 'signed_up', label: 'Signed Up', color: '#D4AF37', icon: CheckCircle },
  { value: 'lost_cold', label: 'Lost/Cold', color: '#EF4444', icon: XCircle },
];

const SOURCES = [
  { value: 'contact_form', label: 'Contact Form' },
  { value: 'membership_form', label: 'Membership Form' },
  { value: 'manual_entry', label: 'Manual Entry' },
  { value: 'referral', label: 'Referral' },
  { value: 'other', label: 'Other' },
];

const CRMDashboard = () => {
  const [view, setView] = useState('dashboard'); // dashboard, contacts, contact-detail
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [tags, setTags] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [showAddContact, setShowAddContact] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [showAddTag, setShowAddTag] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newTag, setNewTag] = useState({ name: '', color: '#D4AF37' });
  const [newContact, setNewContact] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    source: 'manual_entry',
    source_detail: '',
    stage: 'new_lead',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (view === 'contacts') {
      fetchContacts();
    }
  }, [searchQuery, filterStage, filterSource, view]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, tagsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/crm/stats`),
        axios.get(`${API}/crm/tags`),
        axios.get(`${API}/crm/contacts?limit=100`)
      ]);
      setStats(statsRes.data);
      setTags(tagsRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      console.error('Error fetching CRM data:', error);
      toast.error('Failed to load CRM data');
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    try {
      let url = `${API}/crm/contacts?limit=100`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (filterStage) url += `&stage=${filterStage}`;
      if (filterSource) url += `&source=${filterSource}`;
      
      const res = await axios.get(url);
      setContacts(res.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const handleCreateContact = async () => {
    // Validate required fields
    if (!newContact.first_name.trim()) {
      toast.error('First name is required');
      return;
    }
    if (!newContact.email.trim()) {
      toast.error('Email is required');
      return;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newContact.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    try {
      await axios.post(`${API}/crm/contacts`, newContact);
      toast.success('Contact created successfully');
      setShowAddContact(false);
      setNewContact({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        source: 'manual_entry',
        source_detail: '',
        stage: 'new_lead',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create contact');
    }
  };

  const handleUpdateStage = async (contactId, newStage) => {
    try {
      await axios.patch(`${API}/crm/contacts/${contactId}/stage?stage=${newStage}`);
      toast.success('Stage updated');
      fetchData();
      if (selectedContact?.id === contactId) {
        setSelectedContact({ ...selectedContact, stage: newStage });
      }
    } catch (error) {
      toast.error('Failed to update stage');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedContact) return;
    try {
      await axios.post(`${API}/crm/contacts/${selectedContact.id}/notes`, { content: newNote });
      toast.success('Note added');
      setNewNote('');
      setShowAddNote(false);
      // Refresh contact
      const res = await axios.get(`${API}/crm/contacts/${selectedContact.id}`);
      setSelectedContact(res.data);
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!selectedContact) return;
    try {
      await axios.delete(`${API}/crm/contacts/${selectedContact.id}/notes/${noteId}`);
      toast.success('Note deleted');
      const res = await axios.get(`${API}/crm/contacts/${selectedContact.id}`);
      setSelectedContact(res.data);
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const handleCreateTag = async () => {
    if (!newTag.name.trim()) return;
    try {
      await axios.post(`${API}/crm/tags`, newTag);
      toast.success('Tag created');
      setNewTag({ name: '', color: '#D4AF37' });
      setShowAddTag(false);
      const res = await axios.get(`${API}/crm/tags`);
      setTags(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tag');
    }
  };

  const handleAddTagToContact = async (tagId) => {
    if (!selectedContact) return;
    try {
      await axios.post(`${API}/crm/contacts/${selectedContact.id}/tags/${tagId}`);
      toast.success('Tag added');
      const res = await axios.get(`${API}/crm/contacts/${selectedContact.id}`);
      setSelectedContact(res.data);
    } catch (error) {
      toast.error('Failed to add tag');
    }
  };

  const handleRemoveTagFromContact = async (tagId) => {
    if (!selectedContact) return;
    try {
      await axios.delete(`${API}/crm/contacts/${selectedContact.id}/tags/${tagId}`);
      toast.success('Tag removed');
      const res = await axios.get(`${API}/crm/contacts/${selectedContact.id}`);
      setSelectedContact(res.data);
    } catch (error) {
      toast.error('Failed to remove tag');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    try {
      await axios.delete(`${API}/crm/contacts/${contactId}`);
      toast.success('Contact deleted');
      setSelectedContact(null);
      setView('contacts');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const handleExport = () => {
    let url = `${API}/crm/export`;
    const params = [];
    if (filterStage) params.push(`stage=${filterStage}`);
    if (filterSource) params.push(`source=${filterSource}`);
    if (params.length > 0) url += '?' + params.join('&');
    window.open(url, '_blank');
  };

  const handleSyncContacts = async () => {
    try {
      const res = await axios.post(`${API}/crm/sync`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to sync contacts');
    }
  };

  const getStageInfo = (stage) => STAGES.find(s => s.value === stage) || STAGES[0];
  const getTagById = (tagId) => tags.find(t => t.id === tagId);

  // Navigate to contacts with filter
  const handleStatClick = (stage = null) => {
    if (stage) {
      setFilterStage(stage);
    } else {
      setFilterStage('');
    }
    setFilterSource('');
    setView('contacts');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ============== DASHBOARD VIEW ==============
  const renderDashboard = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '600' }}>CRM Dashboard</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button onClick={handleSyncContacts} variant="outline" style={{ fontSize: '0.875rem' }}>
            Sync Contacts
          </Button>
          <Button onClick={() => setView('contacts')} style={{ background: '#D4AF37', color: '#000' }}>
            <Users size={16} style={{ marginRight: '0.5rem' }} />
            View All Contacts
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          <Card 
            onClick={() => handleStatClick()} 
            style={{ 
              background: 'rgba(59, 130, 246, 0.1)', 
              border: '1px solid rgba(59, 130, 246, 0.3)',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            className="stat-card-clickable"
          >
            <CardContent style={{ padding: '1rem', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#3B82F6' }}>{stats.total_contacts}</div>
              <div style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)' }}>Total Contacts</div>
            </CardContent>
          </Card>
          {STAGES.map(stage => {
            // Map stage values to stats keys
            const statsKeyMap = {
              'new_lead': 'new_leads',
              'contacted': 'contacted',
              'booked_call': 'booked_calls',
              'attended_call': 'attended_calls',
              'signed_up': 'signed_up',
              'lost_cold': 'lost_cold'
            };
            const count = stats[statsKeyMap[stage.value]] || 0;
            
            return (
              <Card 
                key={stage.value} 
                onClick={() => handleStatClick(stage.value)}
                style={{ 
                  background: `${stage.color}15`, 
                  border: `1px solid ${stage.color}50`,
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                className="stat-card-clickable"
              >
                <CardContent style={{ padding: '1rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', fontWeight: '700', color: stage.color }}>
                    {count}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)' }}>{stage.label}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Quick Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '2rem' }}>
          <Card style={{ background: 'rgba(212, 175, 55, 0.1)', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
            <CardContent style={{ padding: '1.5rem' }}>
              <div style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', marginBottom: '0.5rem' }}>This Week</div>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#D4AF37' }}>{stats.contacts_this_week}</div>
              <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>new contacts</div>
            </CardContent>
          </Card>
          <Card style={{ background: 'rgba(212, 175, 55, 0.1)', border: '1px solid rgba(212, 175, 55, 0.3)' }}>
            <CardContent style={{ padding: '1.5rem' }}>
              <div style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', marginBottom: '0.5rem' }}>This Month</div>
              <div style={{ fontSize: '2rem', fontWeight: '700', color: '#D4AF37' }}>{stats.contacts_this_month}</div>
              <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>new contacts</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recent Contacts */}
      <Card style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
        <CardContent style={{ padding: '1.5rem' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>Recent Contacts</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {contacts.slice(0, 5).map(contact => {
              const stageInfo = getStageInfo(contact.stage);
              return (
                <div 
                  key={contact.id}
                  onClick={() => { setSelectedContact(contact); setView('contact-detail'); }}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.75rem',
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                >
                  <div>
                    <div style={{ fontWeight: '500' }}>{contact.first_name} {contact.last_name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>{contact.email}</div>
                  </div>
                  <span style={{
                    padding: '0.25rem 0.75rem',
                    borderRadius: '999px',
                    fontSize: '0.75rem',
                    background: `${stageInfo.color}25`,
                    color: stageInfo.color,
                    border: `1px solid ${stageInfo.color}50`
                  }}>
                    {stageInfo.label}
                  </span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // ============== CONTACTS LIST VIEW ==============
  const renderContactsList = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Button onClick={() => setView('dashboard')} variant="ghost" style={{ padding: '0.5rem' }}>
            <ChevronLeft size={20} />
          </Button>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600' }}>Contacts</h2>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button onClick={handleExport} variant="outline" style={{ fontSize: '0.875rem' }}>
            <Download size={16} style={{ marginRight: '0.5rem' }} />
            Export
          </Button>
          <Button onClick={() => setShowAddContact(true)} style={{ background: '#D4AF37', color: '#000' }}>
            <Plus size={16} style={{ marginRight: '0.5rem' }} />
            Add Contact
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{ flex: '1', minWidth: '200px', position: 'relative' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.5)' }} />
          <Input
            placeholder="Search contacts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ paddingLeft: '40px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
          />
        </div>
        <Select value={filterStage || 'all'} onValueChange={(val) => setFilterStage(val === 'all' ? '' : val)}>
          <SelectTrigger style={{ width: '150px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <SelectValue placeholder="All Stages" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {STAGES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterSource || 'all'} onValueChange={(val) => setFilterSource(val === 'all' ? '' : val)}>
          <SelectTrigger style={{ width: '150px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <SelectValue placeholder="All Sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            {SOURCES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Contacts Table */}
      <Card style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.75rem', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>Name</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.75rem', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>Email</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.75rem', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>Phone</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.75rem', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>Source</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.75rem', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>Stage</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.75rem', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map(contact => {
                const stageInfo = getStageInfo(contact.stage);
                return (
                  <tr 
                    key={contact.id}
                    onClick={() => { setSelectedContact(contact); setView('contact-detail'); }}
                    style={{ 
                      borderBottom: '1px solid rgba(255,255,255,0.05)', 
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '1rem', fontWeight: '500' }}>{contact.first_name} {contact.last_name}</td>
                    <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.7)' }}>{contact.email}</td>
                    <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.7)' }}>{contact.phone || '-'}</td>
                    <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.7)', textTransform: 'capitalize' }}>
                      {contact.source?.replace('_', ' ')}
                      {contact.source_detail && <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}> ({contact.source_detail})</span>}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '999px',
                        fontSize: '0.75rem',
                        background: `${stageInfo.color}25`,
                        color: stageInfo.color,
                        border: `1px solid ${stageInfo.color}50`
                      }}>
                        {stageInfo.label}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem' }}>
                      {formatDate(contact.created_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );

  // ============== CONTACT DETAIL VIEW ==============
  const renderContactDetail = () => {
    if (!selectedContact) return null;
    const stageInfo = getStageInfo(selectedContact.stage);
    
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <Button onClick={() => setView('contacts')} variant="ghost" style={{ padding: '0.5rem' }}>
              <ChevronLeft size={20} />
            </Button>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600' }}>
              {selectedContact.first_name} {selectedContact.last_name}
            </h2>
          </div>
          <Button onClick={() => handleDeleteContact(selectedContact.id)} variant="destructive" style={{ background: '#EF4444' }}>
            <Trash2 size={16} style={{ marginRight: '0.5rem' }} />
            Delete
          </Button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem' }}>
          {/* Main Info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Contact Info Card */}
            <Card style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <CardContent style={{ padding: '1.5rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Contact Information</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.25rem' }}>Email</div>
                    <div>{selectedContact.email}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.25rem' }}>Phone</div>
                    <div>{selectedContact.phone || 'Not provided'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.25rem' }}>Source</div>
                    <div style={{ textTransform: 'capitalize' }}>
                      {selectedContact.source?.replace('_', ' ')}
                      {selectedContact.source_detail && ` (${selectedContact.source_detail})`}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.25rem' }}>Created</div>
                    <div>{formatDate(selectedContact.created_at)}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Notes Card */}
            <Card style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <CardContent style={{ padding: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '600' }}>Notes</h3>
                  <Button onClick={() => setShowAddNote(true)} size="sm" style={{ background: '#D4AF37', color: '#000' }}>
                    <Plus size={14} style={{ marginRight: '0.25rem' }} />
                    Add Note
                  </Button>
                </div>
                
                {showAddNote && (
                  <div style={{ marginBottom: '1rem', padding: '1rem', background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                    <textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Write a note..."
                      style={{
                        width: '100%',
                        minHeight: '80px',
                        padding: '0.75rem',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px',
                        color: '#fff',
                        resize: 'vertical',
                        marginBottom: '0.5rem'
                      }}
                    />
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      <Button onClick={() => { setShowAddNote(false); setNewNote(''); }} variant="ghost" size="sm">Cancel</Button>
                      <Button onClick={handleAddNote} size="sm" style={{ background: '#D4AF37', color: '#000' }}>Save Note</Button>
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {selectedContact.notes?.length > 0 ? (
                    selectedContact.notes.map(note => (
                      <div key={note.id} style={{ padding: '1rem', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', borderLeft: '3px solid #D4AF37' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <p style={{ flex: 1, whiteSpace: 'pre-wrap' }}>{note.content}</p>
                          <Button onClick={() => handleDeleteNote(note.id)} variant="ghost" size="sm" style={{ padding: '0.25rem', opacity: 0.5 }}>
                            <X size={14} />
                          </Button>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginTop: '0.5rem' }}>
                          {formatDate(note.created_at)} • {note.created_by}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{ padding: '2rem', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
                      <MessageSquare size={32} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
                      <p>No notes yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Stage Card */}
            <Card style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <CardContent style={{ padding: '1.5rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>Stage</h3>
                <Select value={selectedContact.stage} onValueChange={(val) => handleUpdateStage(selectedContact.id, val)}>
                  <SelectTrigger style={{ background: `${stageInfo.color}15`, border: `1px solid ${stageInfo.color}50`, color: stageInfo.color }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STAGES.map(s => (
                      <SelectItem key={s.value} value={s.value}>
                        <span style={{ color: s.color }}>{s.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Tags Card */}
            <Card style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <CardContent style={{ padding: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: '600' }}>Tags</h3>
                  <Button onClick={() => setShowAddTag(true)} size="sm" variant="ghost">
                    <Plus size={14} />
                  </Button>
                </div>
                
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
                  {selectedContact.tags?.map(tagId => {
                    const tag = getTagById(tagId);
                    if (!tag) return null;
                    return (
                      <span 
                        key={tagId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '999px',
                          fontSize: '0.75rem',
                          background: `${tag.color}25`,
                          color: tag.color,
                          border: `1px solid ${tag.color}50`
                        }}
                      >
                        {tag.name}
                        <X size={12} style={{ cursor: 'pointer' }} onClick={() => handleRemoveTagFromContact(tagId)} />
                      </span>
                    );
                  })}
                </div>

                <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '1rem' }}>
                  <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.5rem' }}>Add tag</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                    {tags.filter(t => !selectedContact.tags?.includes(t.id)).map(tag => (
                      <span 
                        key={tag.id}
                        onClick={() => handleAddTagToContact(tag.id)}
                        style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '999px',
                          fontSize: '0.75rem',
                          background: 'rgba(255,255,255,0.05)',
                          color: 'rgba(255,255,255,0.7)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          cursor: 'pointer'
                        }}
                      >
                        + {tag.name}
                      </span>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  };

  // ============== ADD CONTACT MODAL ==============
  const renderAddContactModal = () => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <Card style={{ width: '100%', maxWidth: '500px', background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)' }}>
        <CardContent style={{ padding: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600' }}>Add New Contact</h3>
            <Button onClick={() => setShowAddContact(false)} variant="ghost" size="sm">
              <X size={18} />
            </Button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '0.25rem' }}>First Name *</label>
                <Input
                  value={newContact.first_name}
                  onChange={(e) => setNewContact({ ...newContact, first_name: e.target.value })}
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '0.25rem' }}>Last Name</label>
                <Input
                  value={newContact.last_name}
                  onChange={(e) => setNewContact({ ...newContact, last_name: e.target.value })}
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                />
              </div>
            </div>
            <div>
              <label style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '0.25rem' }}>Email *</label>
              <Input
                type="email"
                value={newContact.email}
                onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '0.25rem' }}>Phone</label>
              <Input
                type="tel"
                value={newContact.phone}
                onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '0.25rem' }}>Source</label>
                <Select value={newContact.source} onValueChange={(val) => setNewContact({ ...newContact, source: val })}>
                  <SelectTrigger style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SOURCES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '0.25rem' }}>Stage</label>
                <Select value={newContact.stage} onValueChange={(val) => setNewContact({ ...newContact, stage: val })}>
                  <SelectTrigger style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STAGES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '0.25rem' }}>Initial Note</label>
              <textarea
                value={newContact.notes}
                onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })}
                placeholder="Add an initial note..."
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '0.75rem',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  color: '#fff',
                  resize: 'vertical'
                }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
            <Button onClick={() => setShowAddContact(false)} variant="outline">Cancel</Button>
            <Button onClick={handleCreateContact} style={{ background: '#D4AF37', color: '#000' }}>Create Contact</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // ============== ADD TAG MODAL ==============
  const renderAddTagModal = () => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <Card style={{ width: '100%', maxWidth: '400px', background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)' }}>
        <CardContent style={{ padding: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600' }}>Create New Tag</h3>
            <Button onClick={() => setShowAddTag(false)} variant="ghost" size="sm">
              <X size={18} />
            </Button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '0.25rem' }}>Tag Name</label>
              <Input
                value={newTag.name}
                onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
                placeholder="e.g., VIP, Hot Lead"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.7)', display: 'block', marginBottom: '0.25rem' }}>Color</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {['#D4AF37', '#3B82F6', '#10B981', '#EF4444', '#8B5CF6', '#F59E0B'].map(color => (
                  <div
                    key={color}
                    onClick={() => setNewTag({ ...newTag, color })}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: color,
                      cursor: 'pointer',
                      border: newTag.color === color ? '3px solid #fff' : '3px solid transparent'
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
            <Button onClick={() => setShowAddTag(false)} variant="outline">Cancel</Button>
            <Button onClick={handleCreateTag} style={{ background: '#D4AF37', color: '#000' }}>Create Tag</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // ============== MAIN RENDER ==============
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '40px', height: '40px', border: '3px solid rgba(212, 175, 55, 0.3)', borderTopColor: '#D4AF37', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 1rem' }} />
          <p>Loading CRM...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {view === 'dashboard' && renderDashboard()}
      {view === 'contacts' && renderContactsList()}
      {view === 'contact-detail' && renderContactDetail()}
      {showAddContact && renderAddContactModal()}
      {showAddTag && renderAddTagModal()}
      
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default CRMDashboard;
